import { ApolloClient, gql } from '@apollo/client';
import { cloneDeep } from 'lodash';
import update from 'immutability-helper';
import { getApolloClientSingleton } from 'lib/apollo/client/browser';

import { currentReactionSummaryVersion, saveReactionSummary } from 'chat/components/Reaction';
import { FIRST_PAGE_VAR as ANNOUNCEMENT_FIRST_PAGE_VAR } from 'chat/components/AnnouncementStream/components/AnnouncementStreamContainer';
import { FIRST_PAGE_VAR as CHAT_FIRST_PAGE_VAR } from 'chat/components/ChatStream/components/ChatStreamContainer';
import {
  addStreamSequenceItem,
  updateChatStreamPreview,
} from 'chat/components/ChatStream/utils/actions';
import { updateEntityStreamPreview } from 'chat/components/AnnouncementStream/utils/actions';
import ChatMessage from 'entities/ChatMessage';
import GET_ANNOUNCEMENT_STREAM_ITEMS_QUERY from 'chat/components/AnnouncementStream/queries/GetAnnouncementStreamItems.gql';
import type {
  GetAnnouncementStreamItems,
  GetAnnouncementStreamItemsVariables,
} from 'chat/components/AnnouncementStream/queries/__generated__/GetAnnouncementStreamItems';
import GET_CHAT_STREAM_ITEMS_QUERY from 'chat/components/ChatStream/queries/GetChatStreamItems.gql';
import type {
  GetChatStreamItems,
  GetChatStreamItemsVariables,
} from 'chat/components/ChatStream/queries/__generated__/GetChatStreamItems';
import STREAM_SECTION_CHATS_QUERY from 'chat/components/StreamSectionList/queries/StreamSectionChatsQuery.gql';
import type {
  StreamSectionChats,
  StreamSectionChatsVariables,
} from 'chat/components/StreamSectionList/queries/__generated__/StreamSectionChats';

import { PushEvents } from 'pusher/PusherConstants';
import UNREADS from 'components/Navigation/Unreads.gql';
import { UnreadsQuery } from 'components/Navigation/__generated__/UnreadsQuery';
import { isChatMessageChange, isStreamMessageChange } from 'pusher/types';
import type {
  PusherActionV3,
  PusherEventV2,
  StreamMessageChange,
  ChatMessageChange,
} from 'pusher/types';

type Options = {
  client: ApolloClient<any>;
};

class Handler {
  client: ApolloClient<any>;

  constructor(options: Options) {
    this.client = options.client;
  }

  async insertIntoEntityStream(
    streamUUID: string,
    item: StreamMessageChange | ChatMessageChange
  ): Promise<void> {
    try {
      const streamIsLoaded = this.client.readQuery<
        GetAnnouncementStreamItems,
        GetAnnouncementStreamItemsVariables
      >({
        query: GET_ANNOUNCEMENT_STREAM_ITEMS_QUERY,
        variables: { uuid: streamUUID, ...ANNOUNCEMENT_FIRST_PAGE_VAR },
      });

      if (streamIsLoaded) {
        await this.client.query<GetAnnouncementStreamItems, GetAnnouncementStreamItemsVariables>({
          fetchPolicy: 'network-only',
          query: GET_ANNOUNCEMENT_STREAM_ITEMS_QUERY,
          variables: {
            uuid: streamUUID,
            ...ANNOUNCEMENT_FIRST_PAGE_VAR,
          },
        });
      }

      updateEntityStreamPreview(streamUUID, new Date(), item.preview);
    } catch (e) {
      console.log(
        'ApolloPushHandler - stream does not exist or is not yet loaded in the store',
        streamUUID,
        e
      );
    }
  }

  async insertIntoChatStream(
    chatUUID: string,
    item: StreamMessageChange | ChatMessageChange
  ): Promise<void> {
    const message = ChatMessage.fromJSON(item);
    addStreamSequenceItem(chatUUID, (message as any).toGraphQL());
    updateChatStreamPreview(chatUUID, new Date(), item.preview);
    try {
      const res = await this.client.query<GetChatStreamItems, GetChatStreamItemsVariables>({
        query: GET_CHAT_STREAM_ITEMS_QUERY,
        variables: {
          uuid: chatUUID,
          ...CHAT_FIRST_PAGE_VAR,
        },
      });

      const newStreams = res.data.chatStreams;
      const streamRelatedGroups = newStreams?.[0]?.groups;
      if (!streamRelatedGroups) {
        return;
      }

      // update the stream sections for any groups that
      // the fetched stream is related to if the fetched
      // stream is not yet in the stream section (ie.
      // mascot new chat streams)
      const groupIds = streamRelatedGroups.map((group) => group.id);
      groupIds.forEach((id) => {
        let chatStreamsByGroupId: StreamSectionChats | null = null;
        try {
          chatStreamsByGroupId = getApolloClientSingleton().readQuery<
            StreamSectionChats,
            StreamSectionChatsVariables
          >({
            query: STREAM_SECTION_CHATS_QUERY,
            variables: { groupId: id },
          });
        } catch (e) {
          // do nothing
        }
        if (!chatStreamsByGroupId) {
          return;
        }

        const newStreamIsAlreadyInStreamSection = chatStreamsByGroupId.chatStreams?.find(
          (stream) => stream.uuid === chatUUID
        );

        if (newStreamIsAlreadyInStreamSection) {
          return;
        }

        getApolloClientSingleton().writeQuery<StreamSectionChats, StreamSectionChatsVariables>({
          query: STREAM_SECTION_CHATS_QUERY,
          variables: { groupId: id },
          data: update(chatStreamsByGroupId, {
            chatStreams: {
              $unshift: newStreams,
            },
          }),
        });
      });
    } catch (e: any) {
      console.warn({ e, chatUUID });
      console.error('ApolloPushHandler - runtime error fetching GET_CHAT_STREAM_ITEMS_QUERY');
    }
  }

  __handleChatMessageAndBroadcastReaction(push: PusherActionV3<any>) {
    push.changes.forEach((change) => {
      let currentVersion;

      try {
        currentVersion = currentReactionSummaryVersion(change.content.message_uuid);
      } catch (e) {
        currentVersion = -1;
      }

      if (change.content.version <= currentVersion) {
        return;
      }

      const reactionSummaryGQL: any = {
        __typename: 'ReactionSummary',
        version: change.content.version,
        messageUuid: change.content.message_uuid,
        reactions: change.content.reactions.map((reaction: any) => {
          return {
            __typename: 'Reaction',
            name: reaction.name,
            count: reaction.count,
            users: reaction.users.map((user: any) => {
              return {
                __typename: 'ReactionUserSummary',
                uuid: user.uuid,
                name: user.name,
                initials: user.initials,
                color: user.color,
              };
            }),
            hasReacted: reaction.has_reacted,
          };
        }),
      };

      saveReactionSummary(reactionSummaryGQL);
    });
  }

  async __handleCreateChatMessageChangeset(push: PusherActionV3<ChatMessageChange>): Promise<void> {
    const message = push.changes[0].content;
    const messageType = message.type;
    const chatUUID = message.chat?.uuid;
    // TODO I don't see where does thread is defined in the be
    // @ts-expect-error
    const threadUUID = message.thread?.uuid;

    if (messageType === 'broadcast') {
      // if the message is a broadcast, check if the chat with the entity exists in the Apollo store
      this.insertIntoEntityStream(chatUUID, message);
      // check if the announcement stream for the entity exists in the Apollo store
      if (threadUUID) {
        this.insertIntoEntityStream(threadUUID, message);
      }
    }

    // then, update relevant chatStreams regardless of messageType
    await this.insertIntoChatStream(chatUUID, message);
  }

  __handleCreateStreamMessageChangeset(push: PusherActionV3<StreamMessageChange>) {
    const message = push.changes[0].content;
    const streamUUID = message.stream_uuid;
    this.insertIntoEntityStream(streamUUID, message);
    this.insertIntoChatStream(streamUUID, message);
  }

  async __handleUpdateUnreads(push: PusherActionV3<ChatMessageChange>, refetch: boolean = false) {
    const item = push.changes[0].content;
    const senderId = item?.sender?.id;
    const chatUUID = item?.chat?.uuid;

    try {
      const feature = await this.client.query({
        query: gql`
          query FetchFeature($feature: String!) {
            me {
              id
              uuid
              feature: has_feature(name: $feature)
            }
          }
        `,
        variables: {
          feature: 'org-flex.web.dec-21',
        },
      });

      if (!feature.data?.me?.feature) {
        return;
      }

      if (senderId === feature.data.me.id) {
        return;
      }

      if (window.location.pathname.replace(/\/+$/, '').endsWith(chatUUID)) {
        return;
      }
    } catch (error) {
      console.log('ApolloPushHandler', error);
      return;
    }

    if (refetch) {
      return this.client.query<UnreadsQuery>({
        query: UNREADS,
        fetchPolicy: 'network-only',
      });
    }

    if (!chatUUID) {
      return;
    }

    try {
      const res = await this.client.query<GetChatStreamItems, GetChatStreamItemsVariables>({
        query: GET_CHAT_STREAM_ITEMS_QUERY,
        variables: {
          uuid: chatUUID,
          ...CHAT_FIRST_PAGE_VAR,
        },
      });
      const newStreams = res.data.chatStreams;
      const streamRelatedGroups = newStreams?.[0]?.groups;
      if (!streamRelatedGroups) {
        return;
      }

      const groupUuids = streamRelatedGroups.map((group) => group.uuid);

      // for some mysterious reason in order to mutate and save back this original object
      // we need to clone it first, otherwise the mutation doesn't work
      const unreads = cloneDeep(this.client.readQuery<UnreadsQuery>({ query: UNREADS }));
      unreads?.me?.unreads.group_unreads.forEach((group_unread) => {
        if (group_unread && groupUuids.includes(group_unread.id)) {
          group_unread.unread_count = group_unread.unread_count + 1;
        }
      });

      unreads?.me?.unreads.unreads.forEach((unread) => {
        if (unread?.id === 'user') {
          unread.unread_count = unread.unread_count + 1;
        }
      });

      if (unreads) {
        this.client.writeQuery<UnreadsQuery>({
          query: UNREADS,
          data: unreads,
        });
      }
    } catch (error: any) {
      console.error("couldn't update unreads", error);
    }
  }

  async __handleUpdateStreamSections() {
    getApolloClientSingleton().refetchQueries({ include: [STREAM_SECTION_CHATS_QUERY] });
  }

  handle(push: PusherEventV2 | PusherActionV3<StreamMessageChange | ChatMessageChange>): void {
    if (!('changes' in push)) {
      // we only handle v3 changes for now
      console.error("we don't handle v2 changes", push);
      return;
    }

    try {
      switch (push.type) {
        case 'changeset-create-chat_message': {
          if (isChatMessageChange(push.changes[0].content)) {
            this.__handleUpdateStreamSections();
            this.__handleCreateChatMessageChangeset(push as PusherActionV3<ChatMessageChange>);
            this.__handleUpdateUnreads(push as PusherActionV3<ChatMessageChange>);
          }
          return;
        }
        case 'changeset-update-chat_marker”': {
          if (isChatMessageChange(push.changes[0].content)) {
            this.__handleUpdateStreamSections();
            this.__handleUpdateUnreads(push as PusherActionV3<ChatMessageChange>);
          }
          return;
        }
        case 'changeset-create-stream_message': {
          if (isStreamMessageChange(push.changes[0].content)) {
            this.__handleCreateStreamMessageChangeset(push as PusherActionV3<StreamMessageChange>);
          }
          return;
        }
        case 'changeset-update-chat_message_reaction':
        case 'changeset-update-chat_broadcast_reaction': {
          this.__handleChatMessageAndBroadcastReaction(push);
          return;
        }
        case PushEvents.CHANGESET_UPDATE_CHAT_MARKER: {
          if (isChatMessageChange(push.changes[0].content)) {
            this.__handleUpdateUnreads(push as PusherActionV3<ChatMessageChange>, true);
          }
          return;
        }
        default: {
          return;
        }
      }
    } catch (e) {
      console.error('apollo - error while responding to push event ', push, e);
    }
  }
}

export default Handler;
